import { fetchFromServer, syncDependency, type DependencySyncConfig } from "./dependency-sync";
import { products } from "@/services/cache";
import { productsSyncKey } from "@/types/products/product-keys";
import type { ApiFetchResponse } from "@tmi/types/api/fetch";
import type { Products } from "@tmi/types/api/content-models";
import type { Product } from "@tmi/types/entities";

const PAGE_SIZE = 25;

export const config: DependencySyncConfig<Products, Product, Product> = {
    name: 'product',
    store: () => products,
    syncKey: productsSyncKey,
    throttle: { hours: 4 },
    pageSize: PAGE_SIZE,
    // R26 trap 2: `skip` is a PAGE INDEX on the content catalogs, not a row offset — the producer
    // asserts it (UngatedCatalogPagingTests). `page * pageSize` returns one record in every
    // pageSize and looks perfectly healthy from here.
    buildRequest: ( page: number, pageSize: number ) => ( {
        endpoint: `content/product-catalog?skip=${ page }&limit=${ pageSize }`,
        verbOverride: 'GET'
    } ),
    // R26 trap 1: the content twins carry NO `totalCount`. Copying the sentient tickers
    // `isComplete` — `fetchedSoFar >= ( totalCount ?? MAX_SAFE_INTEGER )` — would never once be
    // true here, so every sweep would be permanently partial: never pruning, and re-running on
    // the 15-second partial-retry threshold forever. Completion is "the last page was short",
    // which is also the paging loop's own exit condition.
    isComplete: ( response: ApiFetchResponse<Products> ) => ( response?.data?.productCatalog?.products?.length ?? 0 ) < PAGE_SIZE,
    // The content twins send no ETag, and a 200 carrying `[]` from Contentful is not trustworthy
    // evidence that the catalog is empty — so no ETag alarm, and no whole-store prune.
    expectsEtag: false,
    extractPayload: ( response: ApiFetchResponse<Products> ) => response?.data?.productCatalog?.products,
    mapItem: ( p: Product ): Product => p,
    postProcess: ( items: Product[] ) =>
        items.sort( ( a, b ) =>
            a.name.toLowerCase().localeCompare( b.name.toLowerCase() )
        )
};

/**
 * The whole catalog, or [] when the sweep lost a page. goFetch resolves most failures as null, which ends the sweep as
 * partial, and a truncated list would replace the catalog shown (UX roadmap #83): [] tells the caller to keep its own.
 */
export async function refreshProductsFromServer(): Promise<Product[]> {
    const { items, complete } = await fetchFromServer( config );
    return complete ? items : [];
}

export async function loadProductMap( productsMap: Map<string, Product> ): Promise<boolean> {
    return syncDependency( config, productsMap );
}
